import { commentOnHomeworkInputSchema } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { Alert, Button, Drawer, Input, Space, Spin, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { HOMEWORK_COMMENT_FIELDS, type HomeworkCommentRow, type HomeworkRow } from './model';
import { useHomeworkCommand } from './useHomeworkCommand';

/**
 * E34 · the hub's Comments and Comment sections (cc-homework, chapter "Homework — Assign & follow
 * (portal, E34)": "Client Hub — Homework — Comments (section)" over HomeworkComments, and "… — Comment
 * (section)" → CommentOnHomework): the thread under ONE assignment — every comment the client and the
 * therapists of the practice wrote, oldest first, with who and when — and the box to answer in.
 *
 * The same command the client uses from the phone (`commentOnHomework`; the caller is the performer). A
 * comment is never edited or removed ("a conversation of record, like a note"), so there is no such button;
 * the therapist's answer clears the row's "awaiting your reply" dot, and the drawer tells the hub so
 * (`onChanged`). How the client learns: a notice naming the therapist and the assignment, never the text
 * (the notifier's commentReply) — the intro line says so. A WITHDRAWN assignment takes no comment
 * (HOMEWORK_CANCELLED) — the box is replaced by that line and the thread stays readable.
 *
 * Every refusal is the API's coded, already-localised message, shown verbatim in the drawer (portal
 * conventions): HOMEWORK_COMMENT_NOT_ALLOWED (the client no longer shares with the therapist),
 * HOMEWORK_COMMENT_TEXT, HOMEWORK_CANCELLED. Anything else is the generic line plus Sentry.
 */
type Props = {
  /** The row whose thread opens; null closes the drawer. */
  row: HomeworkRow | null;
  clientName: string;
  locale: 'en' | 'ru';
  onClose: () => void;
  /** A comment was sent — the hub reads its rows again (the "awaiting your reply" dot clears). */
  onChanged: () => void;
};

const MAX_LENGTH = 2000;

export const HomeworkCommentsDrawer = ({ row, clientName, locale, onClose, onChanged }: Props) => {
  const t = useTranslate();
  const { run } = useHomeworkCommand();
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const assignmentId = row?.assignmentId;
  const { data, isFetching, isError, error, refetch } = useCustom<HomeworkCommentRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'homeworkComments',
      fields: HOMEWORK_COMMENT_FIELDS,
      variables: {
        assignmentId: { name: 'assignmentId', type: 'ID!', value: assignmentId },
      },
    },
    // a thread is read fresh on every open — never an earlier open's cache
    queryOptions: { enabled: Boolean(assignmentId), retry: false, cacheTime: 0 },
  });
  const comments = useMemo(() => data?.data ?? [], [data]);

  useEffect(() => {
    setText('');
    setProblem(null);
  }, [assignmentId]);

  useEffect(() => {
    if (isError && !graphqlErrorInfo(error).code) Sentry.captureException(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // the newest comment is what the therapist came to answer
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [comments.length]);

  const dateLocale = locale === 'ru' ? ru : enUS;
  const when = (iso: string) =>
    format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale: dateLocale });

  const withdrawn = row?.status === 'cancelled';
  const over = text.length > MAX_LENGTH;

  const send = async () => {
    if (!row || sending) return;
    setProblem(null);
    const input = { assignmentId: row.assignmentId, text };
    // both ends validate with the shared codec; blank text is the server's HOMEWORK_COMMENT_TEXT line
    if (!text.trim() || over || !commentOnHomeworkInputSchema.isValidSync(input)) {
      setProblem(t('homework.errors.HOMEWORK_COMMENT_TEXT'));
      return;
    }
    setSending(true);
    const r = await run('commentOnHomework', input, t('homework.comments.hub.failed'));
    setSending(false);
    if (!r.ok) {
      setProblem(r.message);
      return;
    }
    setText('');
    await refetch();
    onChanged();
  };

  return (
    <Drawer
      open={Boolean(row)}
      onClose={onClose}
      width={520}
      destroyOnClose
      title={
        <Space direction="vertical" size={0}>
          <span>{t('homework.comments.hub.title')}</span>
          <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            {row?.title}
          </Typography.Text>
        </Space>
      }
      extra={<Button onClick={onClose} data-testid="homework-comments-close">{t('homework.comments.hub.close')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="homework-comments-drawer">
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {t('homework.comments.hub.intro', { name: clientName })}
        </Typography.Paragraph>

        {isError ? (
          <Alert
            type="error"
            showIcon
            message={t('homework.comments.hub.loadFailed')}
            data-testid="homework-comments-load-failed"
          />
        ) : isFetching && comments.length === 0 ? (
          <div style={{ textAlign: 'center' }}>
            <Spin data-testid="homework-comments-loading" />
          </div>
        ) : comments.length === 0 ? (
          <Typography.Text type="secondary" data-testid="homework-comments-empty">
            {t('homework.comments.hub.empty')}
          </Typography.Text>
        ) : (
          <Space direction="vertical" size="small" style={{ width: '100%' }} data-testid="homework-comments-thread">
            {comments.map((c) => (
              <div
                key={c.commentId}
                data-testid={`homework-comment-${c.commentId}`}
                data-side={c.fromClient ? 'client' : 'therapist'}
                style={{
                  background: c.fromClient ? 'rgba(0,0,0,0.04)' : 'rgba(22,119,255,0.08)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginLeft: c.fromClient ? 0 : 32,
                  marginRight: c.fromClient ? 32 : 0,
                }}
              >
                <Space size={8} wrap style={{ fontSize: 12 }}>
                  <Typography.Text strong data-testid={`homework-comment-author-${c.commentId}`}>
                    {c.mine
                      ? t('homework.comments.hub.you')
                      : c.fromClient
                        ? t('homework.comments.hub.client', { name: c.performerName || clientName })
                        : c.performerName || t('homework.comments.hub.therapist')}
                  </Typography.Text>
                  <Typography.Text type="secondary">{when(c.occurredAt)}</Typography.Text>
                </Space>
                <Typography.Paragraph
                  style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}
                  data-testid={`homework-comment-text-${c.commentId}`}
                >
                  {c.text}
                </Typography.Paragraph>
              </div>
            ))}
            <div ref={endRef} />
          </Space>
        )}

        {withdrawn ? (
          <Alert type="info" showIcon message={t('homework.comments.hub.cancelled')} data-testid="homework-comments-cancelled" />
        ) : (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Input.TextArea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder={t('homework.comments.hub.placeholder', { name: clientName })}
              data-testid="homework-comment-input"
              status={over ? 'error' : undefined}
            />
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Typography.Text type={over ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                {t('homework.comments.hub.counter', { count: text.length })}
              </Typography.Text>
              <Button
                type="primary"
                loading={sending}
                disabled={!text.trim() || over}
                onClick={send}
                data-testid="homework-comment-send"
              >
                {t('homework.comments.add')}
              </Button>
            </Space>
            {problem ? (
              <Alert type="error" showIcon message={problem} data-testid="homework-comment-error" />
            ) : null}
          </Space>
        )}
      </Space>
    </Drawer>
  );
};
